/**
 * <s-category-tabs> turns the homepage category bar into in-page tabs.
 * Clicking a category link fetches that collection's "collection-grid" partial
 * and shows it directly under the bar, instead of navigating away.
 * Clicking the active category again closes the panel.
 * Modifier clicks (new tab) and fetch failures fall back to normal navigation.
 *
 * Contract:
 *   [data-category-link]  — category links (plain <a href> to the collection)
 *   [data-category-default-link] — the tab for the default region (Black Friday → T-shirts); active on load
 *   [data-category-panel] — wrapper shown/hidden around the "collection-grid" partial
 *   [data-category-default] — region (Black Friday T-shirts) hidden while a category is open, so products aren't shown twice
 *   "collection-grid"     — must match {% partial 'collection-grid' %} in templates/collection.liquid and templates/index.liquid
 *   data-updated-message / data-error-message — translated announcements
 */
import { partials } from '@shopify/partial-rendering';
import { announceGlobalLiveRegion } from '@theme/live-region';
import { transition } from '@theme/view-transition';

const ACTIVE = ['border-b', 'border-on-surface', 'pb-1', 'text-on-surface'];
const INACTIVE = ['text-on-surface-secondary'];

class CategoryTabs extends HTMLElement {
  #controller = null;
  #active = null;

  #onClick = (event) => {
    const link = event.target.closest('[data-category-link]');
    if (!link || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    // The Latest drop tab, or a second tap on the open category, returns to Black Series.
    if (link.hasAttribute('data-category-default-link') || link === this.#active) {
      this.#close();
      return;
    }
    this.#open(link);
  };

  #scroller = null;
  #resizeObserver = null;

  // Marks the category scroller with data-at-end once nothing is left to swipe to,
  // so the right-edge fade (a "more categories" hint) disappears.
  #updateEdge = () => {
    const el = this.#scroller;
    if (!el) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    el.toggleAttribute('data-at-end', atEnd);
  };

  connectedCallback() {
    this.#active = this.querySelector('[data-category-default-link]');
    this.addEventListener('click', this.#onClick);
    this.#scroller = this.querySelector('[data-category-scroller]');
    if (this.#scroller) {
      this.#scroller.addEventListener('scroll', this.#updateEdge, { passive: true });
      this.#resizeObserver = new ResizeObserver(this.#updateEdge);
      this.#resizeObserver.observe(this.#scroller);
      this.#updateEdge();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#scroller?.removeEventListener('scroll', this.#updateEdge);
    this.#resizeObserver?.disconnect();
    this.#controller?.abort();
  }

  #toggleDefault(visible) {
    for (const region of this.querySelectorAll('[data-category-default]')) region.hidden = !visible;
  }

  #setActive(link) {
    for (const item of this.querySelectorAll('[data-category-link]')) {
      const label = item.querySelector('span');
      const on = item === link;
      if (on) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
      label?.classList.remove(...(on ? INACTIVE : ACTIVE));
      label?.classList.add(...(on ? ACTIVE : INACTIVE));
    }
    this.#active = link;
  }

  #close() {
    this.#controller?.abort();
    const panel = this.querySelector('[data-category-panel]');
    if (panel) panel.hidden = true;
    this.#toggleDefault(true);
    this.#setActive(this.querySelector('[data-category-default-link]'));
  }

  async #open(link) {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const panel = this.querySelector('[data-category-panel]');
    this.#setActive(link);
    this.setAttribute('aria-busy', 'true');
    try {
      const html = await partials.fetch('collection-grid', { url: link.href, signal: controller.signal });
      if (controller.signal.aborted) return;
      await transition(() => {
        partials.apply(html);
        if (panel) panel.hidden = false;
        this.#toggleDefault(false);
      });
      announceGlobalLiveRegion(this.dataset.updatedMessage || '');
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('[s-category-tabs] Partial fetch failed:', error);
      window.location.assign(link.href);
    } finally {
      if (this.#controller === controller) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-category-tabs')) customElements.define('s-category-tabs', CategoryTabs);
